import { useMemo } from 'react'
import { sample } from 'lodash'

import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { Link, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

const deductions = {
  standard: [
    'your Heard subscription fees',
    'payment processor fees (e.g. Square, Stripe)',
    'booking and billing software',
    'tax preparation fees',
    'professional liability insurance',
    'general liability insurance',
    'bank fees (e.g. overdraft and maintenance)',
    'business registration and license fees',
    'depreciation of office furniture (exceeding $2,500)',
    'legal fees',
  ],
  marketing: [
    'Mail and print ads',
    'Online advertising',
    'Website design',
    'Website hosting fees',
    'Professional headshots',
    'Logo design',
    'Search engine optimization tools',
    'Business cards',
    'Marketing brochures',
    'Paid sponsorships',
    'Promotional items (e.g. pens or notepads)',
    'Monthly fees for directory listings (e.g. Psychology Today)',
    'Postage and freight',
  ],
  travel: [
    'baggage fees',
    'rental car costs',
    'rideshare and taxi services',
    'laundry and dry cleaning',
    '50% of meals eaten',
    'wifi access',
    'equipment rental for presentations',
    'lodging',
    'personal vehicle use and maintenance',
  ],
  meals: [
    'coffee or meals with business partners or co-workers',
    'meals for employees and contractors',
    'lunch meetings with potential clients',
    'networking event meals',
  ],
  education: [
    'professional conferences',
    'clinical trainings',
    'seminars and workshops',
  ],
  educationSupplies: [
    'tuition, books, and supplies',
    'online courses',
    'tutors, typists, or researchers',
  ],
  supervision: [
    'training or lessons to maintain your professional certification',
    'upgrading your level of certification within your field',
  ],
  housing: [
    'rent or leases for your office',
    'utilities (heat, water, electricity, internet, phone) for your office',
    'office moving expenses',
    'mortgage payments and utilities for a home office',
  ],
  officeSupplies: [
    'stationery',
    'computer equipment',
    'printers and photocopiers',
    'phones',
    'printer or photocopier ink or toner',
    'printer paper',
    'small furniture items',
    'artwork',
    'storage bins',
  ],
  aids: [
    'toys and games',
    'art therapy supplies and equipment',
    'pressure vests and weighted blankets',
    'prompt cards',
    'instruments for music therapy',
    'workbooks and books',
    'CDs and DVDs',
  ],
}

const DEDUCTION_TIPS = [
  {
    tip: (deduction: string) => (
      <>
        You can write off <b>{deduction}</b> as a deductible expense.
      </>
    ),
    deductions: deductions.standard,
    articleLink:
      'https://www.joinheard.com/articles/the-complete-list-of-tax-deductions-for-therapists',
  },
  {
    tip: (deduction: string) => (
      <>
        You can write off <b>{deduction}</b> as a marketing and advertising
        deductible expense for your practice.
      </>
    ),
    deductions: deductions.marketing,
    articleLink:
      'https://www.joinheard.com/articles/the-complete-list-of-tax-deductions-for-therapists',
  },
  {
    tip: (deduction: string) => (
      <>
        When traveling for business, you can write off 50% of <b>{deduction}</b>{' '}
        as a deductible expense.
      </>
    ),
    deductions: deductions.travel,
    articleLink:
      'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-business-travel',
    hideForScorp: true,
  },
  {
    tip: (deduction: string) => (
      <>
        You can write off <b>{deduction}</b> as a deductible business expense
        for your practice.
      </>
    ),
    deductions: deductions.meals,
    articleLink:
      'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-business-meals',
  },
  {
    tip: (deduction: string) => (
      <>
        As a certified therapist, you can write off <b>{deduction}</b> as a
        continuing education deductible expense, as well as{' '}
        <b>travel to and from these events.</b>
      </>
    ),
    deductions: deductions.education,
    articleLink:
      'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-education-expenses',
  },
  {
    tip: (deduction: string) => (
      <>
        As a licensed therapist, you can write off <b>{deduction}</b> as a
        continuing education deductible expense.
      </>
    ),
    deductions: deductions.educationSupplies,
    articleLink:
      'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-education-expenses',
  },
  {
    tip: () => (
      <>
        <b>Personal therapy</b> for yourself counts as continuing education, and
        can be claimed as a tax deduction, including{' '}
        <b>travel to your sessions</b>.
      </>
    ),
    deductions: [],
    articleLink:
      'https://www.joinheard.com/articles/is-going-to-therapy-tax-deductible',
  },
  {
    tip: (deduction: string) => (
      <>
        As a certified therapist, clinical supervision can be written off as a
        tax deduction if it is for <b>{deduction}</b>.
      </>
    ),
    deductions: deductions.supervision,
    articleLink:
      'https://www.joinheard.com/articles/is-clinical-supervision-tax-deductible-for-therapists',
  },
  {
    tip: (deduction: string) => (
      <>
        You can write off <b>{deduction}</b> as a deductible expense.
      </>
    ),
    deductions: deductions.housing,
    articleLink:
      'https://www.joinheard.com/articles/what-therapists-need-to-know-about-the-home-office-deduction',
    hideForScorp: true,
  },
  {
    tip: (deduction: string) => (
      <>
        You can write off office supplies like <b>{deduction}</b> as a
        deductible expense.
      </>
    ),
    deductions: deductions.officeSupplies,
    articleLink:
      'https://www.joinheard.com/articles/the-complete-list-of-tax-deductions-for-therapists',
  },
  {
    tip: (deduction: string) => (
      <>
        You can write off therapeutic aids like <b>{deduction}</b> as a
        deductible expense.
      </>
    ),
    deductions: deductions.aids,
    articleLink:
      'https://www.joinheard.com/articles/the-complete-list-of-tax-deductions-for-therapists',
  },
]

export const TaxDeductionTipCard = () => {
  const isScorp = useReselector(selectIsCurrentUserScorp)

  const randomTip = useMemo(
    () =>
      sample(
        DEDUCTION_TIPS.filter(({ hideForScorp }) => !hideForScorp || !isScorp)
      ),
    [isScorp]
  )

  const tipText = useMemo(() => {
    if (!randomTip) {
      return ''
    }

    const tip = randomTip.tip

    const randomDeduction =
      randomTip.deductions.length > 0
        ? randomTip.deductions[
            Math.floor(Math.random() * randomTip.deductions.length)
          ]
        : ''

    return tip(randomDeduction)
  }, [randomTip])

  return (
    <div className="p-6 space-y-2">
      <Text variant={TypographyStyles.H3}>Tax Deduction Tip</Text>

      <div>
        <Text className="inline">{tipText}</Text>{' '}
        <Link
          href={randomTip?.articleLink ?? ''}
          newPage
          variant="secondary"
          className="inline"
          trackMessage="Accessed Tax Deduction Tip article"
        >
          View article →
        </Link>
      </div>
    </div>
  )
}
